import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useAuth } from './auth-context'
import makeAuthorizedRequest from '../core/api'
import { FORBIDDEN_EVENT, NO_ACCESS, computeScAccess, isSuperAdminRole, pickUserRecord } from '../core/access'

const PermissionsContext = createContext({ ...NO_ACCESS, status: 'loading', reload: () => {} })

// Re-reading on every refused request would hammer /user when a page fires
// several calls at once; one re-read per this many ms is plenty.
const FORBIDDEN_RELOAD_MS = 5000

// appRoleId undefined = not known from the database yet (use the token's).
const EMPTY = { status: 'loading', permissions: null, appRoleId: undefined, email: null }

/**
 * Loads the signed-in user's app role permissions (GET /user?email=, the same
 * call the staffing-app makes) and exposes what they may do in ShortCircuit
 * (core/access.js). Re-reads when the token's role changes and after the API
 * refuses a request.
 *
 * - The first load failing gives everyone but the super admin no access
 *   (status 'error', with reload) rather than access they may not have.
 * - A re-read failing keeps the permissions already loaded, so a network blip
 *   never closes the page someone is working in.
 * - A different signed-in user starts from nothing, never from the previous
 *   user's permissions.
 */
export const PermissionsProvider = ({ children }) => {
  const { idToken, user, isAuthenticated } = useAuth()
  const email = user?.email || null
  const userId = user?.userId || user?.id || null
  const tokenRoleId = user?.appRoleId || null
  const [state, setState] = useState(EMPTY)
  const [version, setVersion] = useState(0)
  const tokenRef = useRef(idToken)
  tokenRef.current = idToken
  const lastForbiddenReload = useRef(0)

  const reload = useCallback(() => setVersion((v) => v + 1), [])

  useEffect(() => {
    if (!isAuthenticated) {
      setState(EMPTY)
      return undefined
    }
    if (!email) {
      // Signed in but no email to look up: cannot be checked.
      setState({ ...EMPTY, status: 'error' })
      return undefined
    }
    let active = true
    // Same user: keep what we have while re-reading. Someone else: start over.
    setState((prev) =>
      prev.email === email && prev.permissions !== null
        ? { ...prev, status: 'refreshing' }
        : { ...EMPTY, email },
    )
    makeAuthorizedRequest(`/user?email=${encodeURIComponent(email)}`, 'GET', null, tokenRef.current)
      .then((payload) => {
        if (!active) return
        const rows = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : null
        // Case-variant duplicate emails exist in users: prefer the row for this user id.
        const record = (rows && userId && rows.find((r) => String(r.id) === String(userId))) || pickUserRecord(payload)
        setState({
          status: 'ready',
          email,
          permissions: record?.permissions || null,
          // The database role (current), not only the token claim (up to 4 h old).
          appRoleId: record ? record.appRoleId || null : undefined,
        })
      })
      .catch((err) => {
        if (!active) return
        console.error('Could not load ShortCircuit permissions:', err)
        setState((prev) =>
          prev.email === email && prev.permissions !== null
            ? { ...prev, status: 'ready' }
            : { ...EMPTY, email, status: 'error' },
        )
      })
    return () => {
      active = false
    }
    // idToken is read through tokenRef: a refreshed token for the same role
    // must not re-fetch, a changed role (tokenRoleId) must.
  }, [isAuthenticated, email, userId, tokenRoleId, version])

  useEffect(() => {
    const onForbidden = () => {
      const now = Date.now()
      if (now - lastForbiddenReload.current < FORBIDDEN_RELOAD_MS) return
      lastForbiddenReload.current = now
      reload()
    }
    window.addEventListener(FORBIDDEN_EVENT, onForbidden)
    return () => window.removeEventListener(FORBIDDEN_EVENT, onForbidden)
  }, [reload])

  // The role: the database value once loaded, else the token's.
  const appRoleId = state.appRoleId !== undefined && state.status !== 'loading' && state.email === email
    ? state.appRoleId
    : tokenRoleId
  const loadedForThisUser = state.email === email
  const access = state.status === 'error' && !isSuperAdminRole(appRoleId)
    ? NO_ACCESS
    : computeScAccess(loadedForThisUser ? state.permissions : null, appRoleId)
  // A super admin needs no /user answer to be let in.
  const status = isSuperAdminRole(appRoleId) && state.status !== 'ready' ? 'ready' : state.status

  return (
    <PermissionsContext.Provider value={{ ...access, status, reload }}>
      {children}
    </PermissionsContext.Provider>
  )
}

PermissionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const usePermissions = () => useContext(PermissionsContext)
